import React, { useState, useEffect, useRef } from 'react';
import StepLayout from '../components/StepLayout';
import { api } from '../api';
import { isTerminalErrorStatus } from '../utils/recoveryState';
import { ID_BADGE_ENABLED, ID_VERIFICATION_AUTOMATED } from '../utils/featureFlags';
import { isUKSeller } from '../utils/sellerRegion';

// iter-26: rebuild per ID Badge Verification UI Spec (image8) +
// operator note "We should ask about what ID they have before this
// step. And only show this if they tick passport".
//
// Layout (top to bottom):
//   1. Heading "ID Badge Verification" (no asterisk)
//   2. Light-green bonus card: $/£5 circle on left + "Earn an extra
//      $5 / Added to your account when verified"
//   3. "HOW TO ADD YOUR ID BADGE" numbered steps (1..4 in circles)
//   4. Orange primary "Yes, I'll add it (+$5)"
//   5. Underlined "No thanks, skip" link below
//
// Conditional rendering: App-level shouldSkip (step 11) skips this
// page when user didn't tick Passport on ID Type or already has the
// verified badge.
//
// NO Trustpilot — focus is the bonus, not social proof.
//
// B-IDBADGE-V2 (2026-06-22): the upsell copy is flag-driven. When
// ID_VERIFICATION_AUTOMATED is ON (default), the page promises an automated,
// scan-a-code flow ("we'll set it up; scan a QR on the next screen") and Yes
// jumps to Step12 which initiates + shows the QR. When OFF, the legacy "open
// LinkedIn and add it yourself" how-to renders verbatim. The accept handler is
// identical in both (updateIDBadge(true) → goToStep(12)) — only the explainer
// copy differs — so this stays one component, gated inline.
export default function Step09IDBadgeUpsell({ sessionId, sessionData, updateData, onNext, goToStep }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileChecked, setProfileChecked] = useState(false);
  const [sessionFailed, setSessionFailed] = useState(null);
  const skippedRef = useRef(false);

  const isGBP = isUKSeller(sessionData);
  const symbol = isGBP ? '£' : '$';
  const priceLabel = `${symbol}5`;

  // B-FE-GATE-2 (2026-05-16): poll /status until V3 login + scrape have
  // produced a definitive verifiedBadgePresent (true/false). Filip's
  // 2026-05-16 live test surfaced this gap: V3 was still polling for
  // app_approval at login when the wizard reached this step, scrape
  // had not run, BE returned verifiedBadgePresent=null, the old code
  // coerced that to false → rendered the upsell on a seller who DID
  // have a badge. The fix: treat null/undefined as "still finalising"
  // and keep polling. Once the field is a real boolean, commit it to
  // sessionData and let the auto-skip useEffect below decide.
  //
  // We poll /status (not /profile) because /status is the canonical
  // source for session-failure detection — if V3 times out, status
  // flips to 'failed' and we can render the error state instead of
  // sitting on a spinner indefinitely.
  useEffect(() => {
    // Already have a definitive value from sessionData hydration —
    // skip the wait.
    if (sessionData?.hasVerifiedBadge !== undefined || sessionData?.verifiedBadgePresent !== undefined) {
      setProfileChecked(true);
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    const poll = async () => {
      if (cancelled) return;
      try {
        const status = await api.getStatus(sessionId);
        if (cancelled) return;

        if (isTerminalErrorStatus(status.status)) {
          setSessionFailed({
            reason: status.failureReason || 'Your login could not be completed.',
          });
          return;
        }

        const badge = status.verifiedBadgePresent;
        if (badge === true || badge === false) {
          // Scrape has run — commit and let auto-skip decide.
          updateData({
            verifiedBadgePresent: badge,
            hasVerifiedBadge: badge,
          });
          setProfileChecked(true);
          return;
        }

        // verifiedBadgePresent still null/undefined → scrape hasn't
        // run yet. Keep waiting.
      } catch (_err) {
        // Transient — retry on next tick.
      }
      timer = setTimeout(poll, 3000);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, updateData, sessionData?.hasVerifiedBadge, sessionData?.verifiedBadgePresent]);

  useEffect(() => {
    if (skippedRef.current) return;
    if (sessionData?.hasVerifiedBadge !== true && sessionData?.verifiedBadgePresent !== true) return;
    skippedRef.current = true;
    (async () => {
      try {
        await api.updateIDBadge(sessionId, { accepted: false });
      } catch (_) { /* non-fatal */ }
      // iter-35: when we auto-skip because the seller already has the
      // badge, fire identityConfirm here too. The upsell-No path and
      // the Step12 Yes-then-added paths both call it; the auto-skip
      // path was missing it, so the email-change worker never
      // dispatched and the seller saw "no email change starting".
      try {
        await api.identityConfirm(sessionId);
      } catch (_) { /* non-fatal — saga retry handles */ }
      updateData({ idBadgeAccepted: false, idBadgeAlreadyHad: true });
      onNext();
    })();
  }, [sessionData, sessionId, updateData, onNext]);

  // B-IDBADGE-GOTOSTEP (2026-06-02): defense-in-depth self-skip. If the
  // ID-badge sub-flow is disabled, never render the upsell even if this step
  // was reached via a direct setStep / resume path that bypassed
  // App.shouldSkip. No updateIDBadge/identityConfirm here — the upsell was NOT
  // offered, so id_badge_offered must stay false; email-change auto-chains
  // server-side and Step13 fires identity_confirm for the legacy path.
  useEffect(() => {
    if (ID_BADGE_ENABLED) return;
    if (skippedRef.current) return;
    skippedRef.current = true;
    onNext();
  }, [onNext]);

  if (!ID_BADGE_ENABLED) return null;

  if (sessionFailed) {
    // V3 login failed before we could decide whether to upsell.
    // Surface the error instead of dropping the seller into a
    // misleading upsell or a silent spinner.
    return (
      <StepLayout title="We hit an issue" hideTrustpilot>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <p style={{ color: 'var(--danger)', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
            Sign-in could not be completed.
          </p>
          <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.5, marginBottom: 24 }}>
            {sessionFailed.reason}
          </p>
          <p style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.5 }}>
            Please contact support. Your details have been saved and we&rsquo;ll
            follow up shortly.
          </p>
        </div>
      </StepLayout>
    );
  }

  if (!profileChecked) {
    // B-FE-GATE-2: render a wait spinner while we poll /status for the
    // scrape result. The seller can sit here for up to ~5 min if V3 is
    // mid-app-approval — that is correct behaviour: we do not want to
    // ask "do you have a badge?" until we actually know the answer.
    return (
      <StepLayout title={null}>
        <style>{`@keyframes step09spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid #ffffff',
            borderTopColor: 'var(--primary)',
            borderRadius: '50%',
            animation: 'step09spin 1s linear infinite',
            margin: '0 auto 24px',
          }} />
          <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            Finalising your sign-in
          </p>
          <p style={{ color: 'var(--text2)', fontSize: 13 }}>
            One moment, checking your LinkedIn account…
          </p>
        </div>
      </StepLayout>
    );
  }

  if (sessionData?.hasVerifiedBadge === true || sessionData?.verifiedBadgePresent === true) {
    return null;
  }

  const handleAccept = async () => {
    setLoading(true);
    setError('');
    try {
      await api.updateIDBadge(sessionId, { accepted: true });
      updateData({ idBadgeAccepted: true, idBadgeUpsell: 'Yes' });
      // iter-27: jump explicitly to step 12 (Waiting for Badge). Without
      // this, App.next() reads stale sessionData in its useCallback
      // closure and skips past step 12 because idBadgeUpsell !== 'Yes'
      // in the closure snapshot taken before this updateData flushed.
      if (goToStep) {
        goToStep(12);
      } else {
        onNext();
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    setError('');
    try {
      await api.updateIDBadge(sessionId, { accepted: false });
      updateData({ idBadgeAccepted: false, idBadgeUpsell: 'No' });
      try { await api.identityConfirm(sessionId); } catch (_) { /* non-fatal */ }
      onNext();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StepLayout title="ID Badge Verification" hideTrustpilot>
      <div className="badge-bonus">
        <div className="badge-bonus__icon" aria-hidden="true">
          <span className="badge-bonus__icon-glyph">{symbol}</span>
        </div>
        <div className="badge-bonus__text">
          <div className="badge-bonus__title">Earn an extra {priceLabel}</div>
          <div className="badge-bonus__sub">Added to your account when verified</div>
        </div>
      </div>

      {ID_VERIFICATION_AUTOMATED ? (
        <div className="badge-howto">
          <div className="badge-howto__label">HOW IT WORKS</div>
          <ol className="badge-howto__list">
            {[
              'Tap “Yes” and we’ll start the verification for you',
              'On the next screen, scan the QR code with your phone',
              'Follow the steps on your phone with your ID, about 2 minutes',
              'Come back and tap “I’ve completed it”',
            ].map((text, i) => (
              <li key={i} className="badge-howto__row">
                <span className="badge-howto__num" aria-hidden="true">{i + 1}</span>
                <span className="badge-howto__text">{text}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <div className="badge-howto">
          <div className="badge-howto__label">HOW TO ADD YOUR ID BADGE</div>
          <ol className="badge-howto__list">
            {[
              'Open the LinkedIn app on your phone',
              'Go to your profile by clicking your picture',
              'Click "Add verification badge" or "Verify now"',
              'Follow the prompts and verify with your passport',
            ].map((text, i) => (
              <li key={i} className="badge-howto__row">
                <span className="badge-howto__num" aria-hidden="true">{i + 1}</span>
                <span className="badge-howto__text">{text}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {error && <p className="form-field__error">{error}</p>}

      <button
        type="button"
        onClick={handleAccept}
        disabled={loading}
        className="primary-cta badge-accept-cta"
      >
        {ID_VERIFICATION_AUTOMATED
          ? `Yes, verify me (+${priceLabel})`
          : `Yes, I’ll add it (+${priceLabel})`}
      </button>

      <button
        type="button"
        onClick={handleSkip}
        disabled={loading}
        className="badge-skip-link"
      >
        No thanks, skip
      </button>
    </StepLayout>
  );
}
